// exportPromotionsToExcel.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'

export interface PromotionExportRow {
    promotionTypeName: string
    promotionCode: string
    promotionName: string
    promotionNameEn: string
    promotionFormat: string
    branchName: string
    count: number
    usageDate: string  // e.g. "07/05/2568 - 31/05/2568"
    status: string
    updatedBy: string
    updatedAt: string  // ISO or "YYYY-MM-DD HH:mm:ss"
}

export interface PromotionExportReport {
    title: string     // e.g. "รายการโปรโมชัน"
    statusFilter: string     // e.g. "-"
    rows: PromotionExportRow[],
    t: any
}

export async function exportPromotionsToExcel(rpt: PromotionExportReport) {
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(`${rpt.title}`)
    ws.properties.defaultRowHeight = 18

    // 1) define columns A–K
    ws.columns = [
        { header: '#', width: 6 },
        { header: rpt.t('PROMOTION.TABLE.PROMOTION_TYPENAME'), width: 20 },
        { header: rpt.t('PROMOTION.TABLE.PROMOTION_CODE'), width: 12 },
        { header: rpt.t('PROMOTION.TABLE.PROMOTION_NAME'), width: 30 },
        { header: rpt.t('PROMOTION.TABLE.PROMOTION_NAMEEN'), width: 30 },
        { header: rpt.t('PROMOTION.TABLE.TYPE'), width: 15 },
        { header: rpt.t('PROMOTION.TABLE.USAGE_BRANCH'), width: 50 },
        { header: rpt.t('PROMOTION.TABLE.COUNT'), width: 10 },
        { header: rpt.t('PROMOTION.TABLE.USAGE_DATE'), width: 20 },
        { header: rpt.t('APPLICATION.TABLE.CELL.STATUS'), width: 15 },
        { header: rpt.t('APPLICATION.TABLE.CELL.UPDATED_BY'), width: 30 }
    ] as ExcelJS.Column[]

    let row = 1

    // 2) Title row (A1–K1)
    ws.mergeCells(row, 1, row, ws.columns.length)
    ws.getCell(row, 1).value = rpt.title
    ws.getCell(row, 1).font = { size: 16, bold: true }
    ws.getCell(row, 1).alignment = { horizontal: 'center', vertical: 'middle' }
    row++

    // 3) Status filter row (A2–K2)
    ws.mergeCells(row, 1, row, ws.columns.length)
    ws.getCell(row, 1).value = `สถานะ: ${rpt.statusFilter}`
    ws.getCell(row, 1).font = { size: 12 }
    ws.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' }
    row += 2

    // 4) Header row
    const header = ws.addRow(ws.columns.map(c => c.header as string))
    header.font = { bold: true }
    header.alignment = { horizontal: 'center', vertical: 'middle' }
    header.height = 20
    header.eachCell(cell => {
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        }
    })
    row++


    if (!rpt.rows || rpt.rows.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, ws.columns.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
        const buf = await wb.xlsx.writeBuffer()
        saveAs(
            new Blob([buf]),
            `${rpt.title}_${moment().format('YYYYMMDD')}.xlsx`
        )
        return
    }

    // 5) Data rows
    rpt.rows.forEach((d, i) => {
        const r = ws.addRow([
            i + 1,
            d.promotionTypeName,
            d.promotionCode,
            d.promotionName,
            d.promotionNameEn,
            d.promotionFormat,
            d.branchName,
            d.count,
            d.usageDate,
            d.status,
            `${d.updatedBy} ${d.updatedAt}`
        ])
        r.alignment = { vertical: 'middle' }
        r.eachCell((cell, col) => {
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            }
            switch (col) {
                case 2:
                case 4:
                case 5:
                case 6:
                case 7:
                    cell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
                    break
                default:
                    cell.alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
            }
        })
    })

    // 6) save
    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `${rpt.title}_${moment().format('YYYYMMDD')}.xlsx`)
}
